import type { IContact } from "../types/Contacts";
import type { IChatMessage, IChatParty } from "../types/IChatMessages";
import type { DirUser, PresenceState, ServerMessage } from "./types";

export const splitName = (name: string, email = ""): { firstName: string; lastName: string } => {
  const n = name.replace(/\s+/g, " ").trim() || email.split("@")[0] || "";
  const i = n.indexOf(" ");
  return i < 0 ? { firstName: n, lastName: "" } : { firstName: n.slice(0, i), lastName: n.slice(i + 1) };
};

export const isOnline = (p: PresenceState | undefined): boolean => !!p && p.status !== "offline" && p.online !== false;

export const toContact = (u: DirUser, presence?: PresenceState): IContact => {
  const { firstName, lastName } = splitName(u.name, u.email);
  const n = Number(u.id);
  return {
    _id: u.id,
    userId: Number.isFinite(n) ? n : 0,
    firstName,
    lastName,
    email: u.email,
    phoneNumber: "",
    profilePicture: null,
    status: isOnline(presence),
  };
};

const party = (c: Pick<IContact, "email" | "phoneNumber" | "firstName" | "lastName" | "profilePicture" | "status">): IChatParty => ({
  email: c.email,
  phoneNumber: c.phoneNumber,
  firstName: c.firstName,
  lastName: c.lastName,
  profilePicture: c.profilePicture,
  status: c.status,
});

export interface MsgContext {
  me: IContact;
  partner: IContact | undefined;
  partnerReadSeq: number;
  sendState?: "sending" | "sent" | "failed" | undefined;
}

export const toChatMessage = (m: ServerMessage, ctx: MsgContext): IChatMessage => {
  const fromMe = m.senderId === ctx.me._id;
  const sn = splitName(m.senderName);
  const other: IContact | undefined = ctx.partner;
  const senderParty: IChatParty = fromMe
    ? party(ctx.me)
    : other && other._id === m.senderId
      ? party(other)
      : { email: "", phoneNumber: "", firstName: sn.firstName, lastName: sn.lastName, profilePicture: null, status: false };
  const receiverParty: IChatParty = fromMe ? (other ? party(other) : party(ctx.me)) : party(ctx.me);
  const file = m.attachments && m.attachments[0];
  const state = ctx.sendState ?? "sent";
  const out: IChatMessage = {
    sender: senderParty,
    receiver: receiverParty,
    message: m.deletedAt ? "" : m.body,
    timestamp: m.createdAt,
    status: state,
    fileType: file?.mime ?? null,
    fileUrl: file ? `/api/v1/files/items/${encodeURIComponent(file.fileId)}/download` : null,
    isEdited: !!m.editedAt,
    isDeleted: !!m.deletedAt,
    reaction: null,
    delivered: state === "sent",
    read: fromMe && m.seq > 0 && ctx.partnerReadSeq >= m.seq,
    createdAt: m.createdAt,
    updatedAt: m.editedAt ?? m.createdAt,
    messageId: m.id,
    senderId: m.senderId,
    seq: m.seq,
    convId: m.convId,
    sendState: state,
  };
  if (m.clientMsgId) out.clientMsgId = m.clientMsgId;
  return out;
};
